import { useState, useEffect, forwardRef } from "react";
import {
  Grid,
  Typography,
  List,
  ListItem,
  Dialog,
  AppBar,
  Toolbar,
  DialogContent,
  Slide,
  IconButton,
  Button,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  Box,
  Paper,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  DialogTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";
import QRCode from "qrcode";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const WaiverDialog = ({ open, close, event, orgId }) => {
  const [waivers, setWaivers] = useState([]);
  const [selectedWaiver, setSelectedWaiver] = useState("");
  const [waiverUrl, setWaiverUrl] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");

  useEffect(() => {
    if (open) {
      console.log(event);
      getWaivers();
    }
  }, [open]);

  useEffect(() => {
    if (open && event && event?.waiver) {
      setSelectedWaiver(event.waiver);
      getWaiverUrl();
    }
  }, [open, event]);

  const getWaivers = async () => {
    const result = await request(`waiver/?unused=true&eventId=${event?._id}`);
    if (result.success) {
      setWaivers(result.data);
    }
  };

  const getWaiverUrl = async () => {
    const result = await request(`waiver/url/?eventId=${event?._id}`);
    if (result.success) {
      setWaiverUrl(result.url);
    }
  };

  const handleSelectWaiver = async (waiverId) => {
    try {
      const body = { eventId: event._id, waiverId, action: "assign" };
      const result = await request("events/waiver", "post", body);
      if (result.success) {
        toast.success("Waiver assigned to event");
        setSelectedWaiver(waiverId);
      } else {
        toast.error(result.error);
      }
    } catch (e) {
      toast.error(`Error updating waiver: ${e.message}`);
    }
  };

  const handleRemove = async () => {
    try {
      const body = { eventId: event._id, action: "remove" };
      const result = await request("events/waiver", "post", body);
      if (result.success) {
        toast.success("Waiver removed from event");
        setSelectedWaiver("");
      } else {
        toast.error(result.error);
      }
    } catch (e) {
      toast.error(`Error removing waiver: ${e.message}`);
    }
  };

  const handleShowQr = async () => {
    if (!waiverUrl) return;
    try {
      const url = await QRCode.toDataURL(waiverUrl);
      setQrDataUrl(url);
      setQrOpen(true);
    } catch (err) {
      toast.error("Error generating QR code");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      TransitionComponent={Transition}
      fullWidth
    >
      <DialogTitle>Manage Event Waiver</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select a waiver for this event
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              marginBottom: "20px",
            }}
          >
            <Select
              value={selectedWaiver}
              onChange={(e) => handleSelectWaiver(e.target.value)}
              fullWidth
              size="small"
              displayEmpty
              renderValue={(selected) =>
                selected === ""
                  ? "No waiver selected"
                  : waivers.find((w) => w._id === selected)?.title || ""
              }
            >
              <MenuItem value="">No waiver selected</MenuItem>
              {waivers &&
                waivers.length > 0 &&
                waivers.map((w) => (
                  <MenuItem key={w._id} value={w._id}>
                    {w.title}
                  </MenuItem>
                ))}
            </Select>
            <Button
              variant="outlined"
              color="error"
              disabled={!selectedWaiver}
              onClick={handleRemove}
            >
              Remove
            </Button>
          </Box>
          {waiverUrl && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                Waiver URL: {waiverUrl}
              </Typography>
            </Box>
          )}
          {waiverUrl && (
            <Box
              sx={{
                mt: 1,
                mb: 2,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button variant="contained" onClick={handleShowQr}>
                Waiver QR Code
              </Button>
            </Box>
          )}
          <Box>
            <Typography>
              Don't see the waiver you are looking for? Each waiver can only be
              used once, so you may need to create a new one or just copy an
              existing one in the Waivers tab
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        fullScreen
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setQrOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              Waiver QR Code
            </Typography>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            p: 2,
          }}
        >
          {qrDataUrl && (
            <img
              src={qrDataUrl}
              alt="Waiver QR Code"
              style={{ width: "80%", maxWidth: 400 }}
            />
          )}
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default WaiverDialog;
